import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {
  ConfigService,
  ConnectedPatientAccount,
  EhiExportOutputType,
  PatientDemographics,
  formatPatientDemographicsAddress,
  formatPatientDemographicsBirthdate,
  formatPatientDemographicsName, ApiMode,
} from '@shared-library';
import {FastenService} from "../../services/fasten.service";

type AccountDetailsApp = {
  name: string;
  websiteUrl: string;
  privacyPolicyUrl: string;
  termsOfUseUrl: string;
  accentClasses: string;
};

type AccountDetailsExportOption = {
  type: EhiExportOutputType;
  label: string;
  description: string;
};

type AccountDetailsActivity = {
  appName: string;
  summary: string;
  timestamp: string;
  status: string;
};

@Component({
  selector: 'app-account-details',
  templateUrl: './account-details.component.html',
  styleUrls: ['./account-details.component.css'],
  standalone: false
})
export class AccountDetailsComponent implements OnInit {
  accountId = '';
  account?: ConnectedPatientAccount;
  institutionWebsite = '';
  consentExpiresLabel = '';
  patientDemographics?: PatientDemographics;
  appsUsingConnection: AccountDetailsApp[] = [];
  activityHistory: AccountDetailsActivity[] = [];
  exportActions: AccountDetailsExportOption[] = [];
  appActionFeedback = '';
  selectedExportType?: EhiExportOutputType;
  isExportModalOpen = false;

  constructor(
    private route: ActivatedRoute,
    public configService: ConfigService,
    public fastenService: FastenService,
  ) {}

  ngOnInit(): void {
    this.accountId = this.route.snapshot.paramMap.get('accountId') || '';
    this.account = this.findSelectedAccount();
    this.institutionWebsite = this.buildInstitutionWebsite();

    const consentExpiresAt = this.account?.consent_expires_at || this.buildMockConsentExpiration();
    this.consentExpiresLabel = this.formatConsentExpiration(consentExpiresAt);

    this.patientDemographics = this.shouldShowPatientDemographics()
      ? (this.configService.vaultProfileConfig$.verifiedIdentityPatientDemographics || this.buildMockPatientDemographics())
      : undefined;

    this.appsUsingConnection = this.buildMockApps();
    this.activityHistory = this.buildMockActivityHistory();
    this.exportActions = [
      {
        type: 'pdf',
        label: 'Export as PDF',
        description: 'Generate a portable summary for download or sharing.',
      },
      {
        type: 'fhir_bundle',
        label: 'Export as Smart Health Link',
        description: 'Create a mock SMART Health Link for app-based retrieval.',
      },
      {
        type: 'cms_patient_shared_health_document',
        label: 'Export as CMS Patient Document',
        description: 'Prepare a mock CMS-compatible patient document package.',
      },
    ];

    this.fastenService.getVaultProfileConnectionInfo(this.account?.vault_profile_connection_id!).subscribe((info) => {
      console.log('Fetched connection info for account details view:', info);
    })

  }

  get accountDisplayName(): string {
    return this.account?.portal?.name || this.account?.brand?.name || 'Connected institution';
  }

  get shouldDisplayPatientDemographicsSection(): boolean {
    return !!this.patientDemographics;
  }

  get formattedPatientName(): string {
    return formatPatientDemographicsName(this.patientDemographics);
  }

  get formattedPatientBirthdate(): string {
    return formatPatientDemographicsBirthdate(this.patientDemographics);
  }

  get formattedPatientAddress(): string {
    return formatPatientDemographicsAddress(this.patientDemographics?.addressInformation?.[0]);
  }

  disconnectMockApp(appName: string): void {
    const confirmed = window.confirm(`Disconnect ${appName} from this connection?`);

    if (!confirmed) {
      return;
    }

    this.appsUsingConnection = this.appsUsingConnection.filter((app) => app.name !== appName);
    this.appActionFeedback = `${appName} has been removed from this mock connection view.`;
  }

  openExportModal(exportType: EhiExportOutputType): void {
    this.selectedExportType = exportType;
    this.isExportModalOpen = true;
  }

  closeExportModal(): void {
    this.isExportModalOpen = false;
    this.selectedExportType = undefined;
  }

  private findSelectedAccount(): ConnectedPatientAccount {
    const connectedAccounts = this.configService.vaultProfileConfig$.connectedPatientAccounts || [];
    const matchingAccount = connectedAccounts.find((candidate) =>
      candidate.org_connection_id === this.accountId
    );

    if (matchingAccount) {
      return matchingAccount;
    }

    return {
      org_connection_id: this.accountId,
      connection_status: 'connected',
      platform_type: 'tefca',
      patient_auth_type: 'tefca_direct',
      vault_profile_connection_id: this.accountId,
      brand: {
        id: 'mock-brand',
        name: 'Connected Institution',
        portals: [],
        hidden: false,
        last_updated: '',
        portal_ids: ['mock-portal'],
      },
      portal: {
        id: 'mock-portal',
        name: 'Institution Portal',
        last_updated: '',
        endpoint_ids: ['mock-endpoint'],
      },
      endpoint: {
        id: 'mock-endpoint',
        platform_type: 'tefca',
      },
    };
  }

  private buildInstitutionWebsite(): string {
    const name = (this.account?.brand?.name || this.account?.portal?.name || 'connected-institution')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '');

    return `https://www.${name || 'connected-institution'}.example.org`;
  }

  private buildMockConsentExpiration(): string {
    const consentDate = new Date();
    consentDate.setDate(consentDate.getDate() + 180);
    return consentDate.toISOString();
  }

  private formatConsentExpiration(consentExpiresAt: string): string {
    const parsedDate = new Date(consentExpiresAt);
    if (Number.isNaN(parsedDate.getTime())) {
      return 'Not available';
    }

    return parsedDate.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  private shouldShowPatientDemographics(): boolean {
    return this.account?.platform_type === 'tefca' || !!this.configService.systemConfig$.tefcaMode;
  }

  private buildMockPatientDemographics(): PatientDemographics {
    return {
      patientName: [{
        given: ['Jordan', 'A.'],
        family: 'Lee',
        suffix: '',
      }],
      birthDate: '1986-04-12',
      gender: 'female',
      addressInformation: [{
        street: '742 Evergreen Terrace',
        unit: 'Unit 3B',
        city: 'Springfield',
        state: 'IL',
        zip: '62704',
        country: 'USA',
        county: 'Sangamon',
      }],
    };
  }

  private buildMockApps(): AccountDetailsApp[] {
    return [
      {
        name: 'Acme Labs',
        websiteUrl: 'https://www.acmelabs.example.com',
        privacyPolicyUrl: 'https://www.acmelabs.example.com/privacy',
        termsOfUseUrl: 'https://www.acmelabs.example.com/terms',
        accentClasses: 'bg-indigo-50 text-indigo-600',
      },
      {
        name: 'Example PHR',
        websiteUrl: 'https://www.examplephr.example.com',
        privacyPolicyUrl: 'https://www.examplephr.example.com/privacy',
        termsOfUseUrl: 'https://www.examplephr.example.com/terms',
        accentClasses: 'bg-emerald-50 text-emerald-600',
      },
      {
        name: 'Clear Claims',
        websiteUrl: 'https://www.clearclaims.example.com',
        privacyPolicyUrl: 'https://www.clearclaims.example.com/privacy',
        termsOfUseUrl: 'https://www.clearclaims.example.com/terms',
        accentClasses: 'bg-sky-50 text-sky-600',
      },
    ];
  }

  private buildMockActivityHistory(): AccountDetailsActivity[] {
    return [
      {
        appName: 'Example PHR',
        summary: `Requested updated encounter data from ${this.accountDisplayName}.`,
        timestamp: '2 hours ago',
        status: 'Completed',
      },
      {
        appName: 'Acme Labs',
        summary: 'Retrieved the latest CCD summary and medication list.',
        timestamp: 'Yesterday',
        status: 'Completed',
      },
      {
        appName: 'Clear Claims',
        summary: 'Checked demographic match confidence for linked coverage.',
        timestamp: '3 days ago',
        status: 'Viewed',
      },
    ];
  }

  protected readonly ApiMode = ApiMode;
}
